import React, { useRef } from 'react';
import { ImageIcon, RefreshCw, Trash2, X } from 'lucide-react';

import { Button } from './ui/button';

/**
 * Tap a photo to see it properly, then replace or delete it.
 *
 * Replaces a hover-reveal delete button, which on a phone meant either an
 * invisible control or one permanently in the way. A photo is now a button:
 * the affordance every phone user already expects from a thumbnail.
 */
const PhotoViewer = ({ photo, onReplace, onDelete, onClose, busy }) => {
  const inputRef = useRef(null);

  if (!photo) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/95">
      <div className="flex items-center justify-between p-4 text-white">
        <p className="text-sm font-medium">{photo.label || 'Photo'}</p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-full p-1.5 hover:bg-white/10"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex flex-1 items-center justify-center overflow-hidden px-4">
        {photo.preview ? (
          <img src={photo.preview} alt={photo.label || ''} className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="flex flex-col items-center gap-2 text-white/60">
            <ImageIcon className="h-8 w-8" />
            <span className="text-sm">Loading…</span>
          </span>
        )}
      </div>

      <div className="flex gap-2 p-4 pb-safe">
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className="flex-1"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          <RefreshCw className="h-4 w-4" />
          Replace
        </Button>

        <Button
          type="button"
          variant="destructive"
          size="lg"
          className="flex-1"
          disabled={busy}
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </Button>
      </div>

      {/* `capture` is absent on purpose: this offers the camera *and* the photo
          library, and which one they want depends on where the car is. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onReplace(file);
        }}
      />
    </div>
  );
};

export default PhotoViewer;
